import React from 'react';
// CI build version - uses local shared components instead of npm package
import { Login, AuthApiClient, useAuth } from 'src/shared';
import type { LoginUserData } from 'src/shared';
import { APP_LOGO_URL as cleverooLogo } from '../constants/brand';

const AdminLogin: React.FC = () => {
  const { login } = useAuth();

  const handleLoginSuccess = (userData: LoginUserData) => {
    const user = {
      ...userData,
      provider: userData.provider as 'local' | 'google' | undefined
    };
    login(user, userData.accessToken);
  };

  // Configure API client for admin endpoints
  const authClient = new AuthApiClient({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080',
    tokenKey: 'admin_token',
    userKey: 'admin_user',
    onUnauthorized: () => {
      // Redirect to login if unauthorized
      window.location.href = '/login';
    }
  });

  const handleLogin = async (credentials: { email: string; password: string }): Promise<LoginUserData> => {
    const response: any = await authClient.login(credentials);
    // Server returns { user: { id, name, email, role, ... }, accessToken, refreshToken }
    // Flatten to match LoginUserData interface
    if (response.user) {
      return { ...response.user, accessToken: response.accessToken, refreshToken: response.refreshToken };
    }
    return response;
  };

  const handleGoogleLogin = async (credential?: string) => {
    if (!credential) {
      throw new Error('No Google credential received.');
    }
    const userData = await authClient.googleAuth(credential);
    handleLoginSuccess(userData);
  };

  const handleForgotPassword = async (email: string): Promise<void> => {
    await authClient.forgotPassword(email);
  };

  const logo = (
    <img 
      src={cleverooLogo} 
      alt="Cleveroo Admin Logo" 
      style={{
        position: 'relative',
        width: '80px',
        height: '80px',
        maxWidth: '80px',
        maxHeight: '80px',
        filter: 'drop-shadow(0 10px 15px rgba(0, 0, 0, 0.2))',
        transition: 'transform 0.3s ease',
        cursor: 'pointer'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
      }}
    />
  );

  return (
    <Login
      onLogin={handleLoginSuccess}
      onGoogleLogin={handleGoogleLogin}
      onForgotPassword={handleForgotPassword}
      title="Cleveroo Admin Panel"
      subtitle="Sign in to access the admin dashboard"
      logo={logo}
      apiLogin={handleLogin}
      showGoogleLogin={true}
      showRegisterLink={false} // Admin accounts are not self-registered
      showForgotPassword={true}
      showRememberMe={true}
      primaryColor="#1890ff"
      secondaryColor="#722ed1"
    />
  );
};

export default AdminLogin;